import { X, AlertTriangle } from 'lucide-react';

interface MaxLengthValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  fieldLabel: string;
  formFieldMaxLength: number;
  crmFieldLength: number;
  crmFieldLabel: string;
}

/**
 * Modal component that warns users when form field max length exceeds CRM field length limits
 */
const MaxLengthValidationModal = ({
  isOpen,
  onClose,
  fieldLabel,
  formFieldMaxLength,
  crmFieldLength,
  crmFieldLabel,
}: MaxLengthValidationModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center text-red-600">
            <AlertTriangle className="h-6 w-6 mr-2" />
            <h2 className="text-xl font-bold text-slate-900">Field Length Mismatch</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-slate-700 mb-4">
            The form field <span className="font-semibold">"{fieldLabel}"</span> cannot be mapped to{' '}
            <span className="font-semibold">{crmFieldLabel}</span> because the form field's maximum
            length <span className="font-semibold">({formFieldMaxLength} characters)</span> exceeds
            the CRM field's maximum length{' '}
            <span className="font-semibold">({crmFieldLength} characters)</span>.
          </p>
          <p className="text-slate-500 text-sm mb-4">
            When users enter data that exceeds the CRM field's limit, the data will be truncated or
            the form submission may fail.
          </p>
          <p className="text-slate-600 text-sm">
            Please either:
            <br />
            • Choose a different CRM field with a higher character limit
            <br />• Increase the CRM field's maximum length to {formFieldMaxLength} characters or
            more
          </p>
        </div>

        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md mb-6">
          <h3 className="text-sm font-semibold text-yellow-800 mb-2">Field Comparison</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Form Field Max Length:</span>
              <span className="font-mono text-red-600">{formFieldMaxLength} characters</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">CRM Field Max Length:</span>
              <span className="font-mono text-green-600">{crmFieldLength} characters</span>
            </div>
            <div className="flex justify-between font-semibold">
              <span className="text-slate-700">Difference:</span>
              <span className="font-mono text-red-600">
                +{formFieldMaxLength - crmFieldLength} characters over limit
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-auto flex justify-end pt-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaxLengthValidationModal;
